import { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useAssets, assetUrl } from "@/context/AssetsContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

const THUMB = "https://images.unsplash.com/photo-1623346483743-b968a27ed34c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxjaW5lbWF0aWMlMjBjYXIlMjBkcml2aW5nJTIwbmlnaHR8ZW58MHx8fHwxNzg2MjI3ODk3fDA&ixlib=rb-4.1.0&q=85";

function toEmbed(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed/${u.pathname.slice(1)}?autoplay=1&mute=1`;
    if (u.hostname.includes("youtube.com")) return `https://www.youtube.com/embed/${u.searchParams.get("v")}?autoplay=1&mute=1`;
    if (u.hostname.includes("vimeo.com")) return `https://player.vimeo.com/video/${u.pathname.split("/").filter(Boolean).pop()}?autoplay=1&muted=1`;
  } catch { /* noop */ }
  return url;
}

export default function VideoSection() {
  const { assets } = useAssets();
  const { config } = useSiteConfig();
  const t = config.t.video;
  const [playing, setPlaying] = useState(false);
  const promo = assets?.promo_video || {};
  const fileUrl = assetUrl(assets, "promo_video");
  const embed = promo.video_url ? toEmbed(promo.video_url) : null;
  const hasVideo = Boolean(fileUrl || embed);

  const thumbEntry = assets?.video_thumbnail;
  const thumb = assetUrl(assets, "video_thumbnail") || THUMB;
  const thumbContain = thumbEntry?.fit === "contain";

  return (
    <section className="bg-[#FAFAFC] py-28 sm:py-36" data-testid="video-section">
      <div className="mx-auto max-w-5xl px-6 text-center">
        <Reveal>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand">{t.eyebrow}</p>
          <h2 className="mx-auto mt-4 max-w-2xl text-4xl font-bold tracking-tighter text-neutral-900 sm:text-5xl lg:text-6xl">
            {t.heading}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-neutral-500">
            {t.paragraph}
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <motion.div whileHover={{ scale: 1.005 }}
            className="group relative mt-14 aspect-video overflow-hidden rounded-3xl shadow-[0_30px_80px_rgba(0,40,120,0.18)]"
            style={{ background: thumbContain ? (thumbEntry?.bg || "#0A0A0A") : "#171717" }}
            data-testid="video-container">
            {playing && fileUrl ? (
              <video src={fileUrl} controls autoPlay muted playsInline className="h-full w-full object-cover" />
            ) : playing && embed ? (
              <iframe src={embed} title="SOSConnect AI video" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="h-full w-full" />
            ) : (
              <>
                <img src={thumb} alt="SOSConnect AI preview" className={`h-full w-full ${thumbContain ? "object-contain" : "object-cover"} opacity-90 transition-transform duration-700 group-hover:scale-105`} loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <button onClick={() => hasVideo && setPlaying(true)}
                  className="absolute left-1/2 top-1/2 grid h-20 w-20 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full glass shadow-xl transition-transform hover:scale-110 active:scale-95"
                  data-testid="video-play-btn" aria-label="Play video">
                  <Play className="ml-1 h-7 w-7 fill-brand text-brand" />
                </button>
                <div className="absolute bottom-5 left-6 text-left text-white/90">
                  <p className="text-sm font-semibold">SOSConnect AI — Official Preview</p>
                  <p className="text-xs text-white/60">{hasVideo ? "Tap to play" : "Promotional video coming soon"}</p>
                </div>
              </>
            )}
          </motion.div>
        </Reveal>
      </div>
    </section>
  );
}
